import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FolderOpen, ArrowLeft, Music, Trash2, Play, Plus, ListMusic, ChevronRight, Pause, Shuffle, Download } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';
import AudioPlayer from '@/components/AudioPlayer';
import ExportModal from '@/components/ExportModal';
import GenreEditor from '@/components/GenreEditor';
import TrackCoverButton from '@/components/TrackCoverButton';
import WaveformBars from '@/components/WaveformBars';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { smartShuffle } from '@/utils/smartShuffle';
import useRestorePlayer from '@/utils/useRestorePlayer';
import { usePlayer } from '@/context/PlayerContext';

// Mini energy wave component for track rows
function MiniEnergyWave({ structure, height = 24, trackId = 'default' }) {
  const gradId = `miniWave-${String(trackId).slice(-8)}`;
  const data = useMemo(() => {
    if (!structure || structure.length === 0) {
      // Deterministic fallback pattern so every track shows something
      return Array.from({ length: 8 }, (_, i) => ({ energy: 35 + ((i * 7 + 13) % 30) }));
    }
    return structure.map((seg) => ({
      energy: seg.energy || 50,
    }));
  }, [structure]);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#2563FF" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#2563FF" stopOpacity={0.2} />
          </linearGradient>
        </defs>
        <Area 
          type="monotone" 
          dataKey="energy" 
          stroke="#2563FF" 
          strokeWidth={1.5} 
          fill={`url(#${gradId})`}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default function CrateDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [crate, setCrate] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingTrackLocal, setPlayingTrackLocal] = useState(null); void playingTrackLocal; void setPlayingTrackLocal;
  // Audio player owned by global PlayerContext now.
  const { playingTrack, isPlaying, playTrack, setIsPlaying } = usePlayer();
  void setIsPlaying;

  // Auto-restore the player after iOS Safari kills the page on long locks.
  useRestorePlayer(tracks, playingTrack, () => {});
  const [showAddTracksDialog, setShowAddTracksDialog] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [availableTracks, setAvailableTracks] = useState([]);
  const [selectedToAdd, setSelectedToAdd] = useState([]);
  
  // Selection state for crate tracks
  const [selectedTracks, setSelectedTracks] = useState([]);

  const toggleCrateSelection = (trackId) => {
    setSelectedTracks(prev =>
      prev.includes(trackId) ? prev.filter(id => id !== trackId) : [...prev, trackId]
    );
  };

  const selectAllCrateTracks = () => {
    if (selectedTracks.length === tracks.length && tracks.length > 0) {
      setSelectedTracks([]);
    } else {
      setSelectedTracks(tracks.map(t => t.id));
    }
  };

  const removeSelectedFromCrate = async () => {
    if (selectedTracks.length === 0) return;
    try {
      for (const trackId of selectedTracks) {
        await axios.delete(`${API}/crates/${id}/tracks/${trackId}`);
      }
      setTracks(prev => prev.filter(t => !selectedTracks.includes(t.id)));
      setCrate(prev => ({
        ...prev,
        track_ids: prev.track_ids.filter(tid => !selectedTracks.includes(tid))
      }));
      toast.success(`Removed ${selectedTracks.length} track${selectedTracks.length !== 1 ? 's' : ''} from crate`);
      setSelectedTracks([]);
    } catch (e) {
      toast.error('Failed to remove tracks');
    }
  };

  // Playlists state
  const [playlists, setPlaylists] = useState([]);
  const [showCreatePlaylist, setShowCreatePlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [selectedTracksForPlaylist, setSelectedTracksForPlaylist] = useState([]);

  const fetchCrate = async () => {
    try {
      const response = await axios.get(`${API}/crates/${id}`);
      setCrate(response.data.crate);
      setTracks(response.data.tracks || []);
    } catch (e) {
      console.error('Failed to fetch crate');
      toast.error('Crate not found');
      navigate('/crates');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaylists = async () => {
    try {
      const response = await axios.get(`${API}/playlists`);
      setPlaylists(response.data.playlists || []);
    } catch (e) {
      console.error('Failed to fetch playlists');
    }
  };

  const fetchAvailableTracks = async () => {
    try {
      const response = await axios.get(`${API}/tracks`);
      const allTracks = response.data.tracks || [];
      // Filter out tracks already in crate
      const crateTrackIds = crate?.track_ids || [];
      setAvailableTracks(allTracks.filter(t => !crateTrackIds.includes(t.id)));
    } catch (e) {
      console.error('Failed to fetch tracks');
    }
  };

  useEffect(() => {
    fetchCrate();
    fetchPlaylists();
  }, [id]);

  useEffect(() => {
    if (showAddTracksDialog) {
      fetchAvailableTracks();
    }
  }, [showAddTracksDialog, crate]);

  const removeTrackFromCrate = async (trackId) => {
    try {
      await axios.delete(`${API}/crates/${id}/tracks/${trackId}`);
      setTracks(prev => prev.filter(t => t.id !== trackId));
      setCrate(prev => ({
        ...prev,
        track_ids: prev.track_ids.filter(tid => tid !== trackId)
      }));
      toast.success('Track removed from crate');
    } catch (e) {
      toast.error('Failed to remove track');
    }
  };

  const addSelectedTracks = async () => {
    if (selectedToAdd.length === 0) {
      toast.error('Please select tracks to add');
      return;
    }

    try {
      await axios.post(`${API}/crates/${id}/tracks/batch`, selectedToAdd);
      toast.success(`Added ${selectedToAdd.length} track(s) to crate`);
      setShowAddTracksDialog(false);
      setSelectedToAdd([]);
      fetchCrate();
    } catch (e) {
      toast.error('Failed to add tracks');
    }
  };

  const toggleTrackSelection = (trackId) => {
    setSelectedToAdd(prev => 
      prev.includes(trackId) 
        ? prev.filter(id => id !== trackId)
        : [...prev, trackId]
    );
  };

  // Playlist functions
  const createPlaylist = async () => {
    if (!newPlaylistName.trim()) {
      toast.error('Please enter a playlist name');
      return;
    }
    try {
      const response = await axios.post(`${API}/playlists`, {
        name: newPlaylistName,
        track_ids: selectedTracksForPlaylist
      });
      toast.success(`Playlist "${newPlaylistName}" created`);
      setNewPlaylistName('');
      setSelectedTracksForPlaylist([]);
      setShowCreatePlaylist(false);
      fetchPlaylists();
    } catch (e) {
      toast.error('Failed to create playlist');
    }
  };

  const addTrackToPlaylist = async (playlistId, trackId) => {
    try {
      await axios.post(`${API}/playlists/${playlistId}/tracks/${trackId}`);
      toast.success('Track added to playlist');
      fetchPlaylists();
    } catch (e) {
      toast.error('Failed to add track to playlist');
    }
  };

  const addAllTracksToPlaylist = async (playlistId) => {
    try {
      for (const track of tracks) {
        await axios.post(`${API}/playlists/${playlistId}/tracks/${track.id}`);
      }
      toast.success(`Added ${tracks.length} tracks to playlist`);
      fetchPlaylists();
    } catch (e) {
      toast.error('Failed to add tracks to playlist');
    }
  };

  const handlePlayTrack = (track) => {
    playTrack(track, tracks);
  };

  const handleShuffle = async () => {
    if (tracks.length < 2) {
      toast.error('Need at least 2 tracks to shuffle');
      return;
    }
    const ids = tracks.map(t => t.id);
    const shuffled = await smartShuffle(ids);
    if (shuffled) {
      const trackMap = {};
      tracks.forEach(t => { trackMap[t.id] = t; });
      const reordered = shuffled.map(id => trackMap[id]).filter(Boolean);
      setTracks(reordered);
      const firstPlayable = reordered.find(t => t.has_audio || t.storage_path);
      if (firstPlayable) {
        playTrack(firstPlayable, reordered.filter(t => t.has_audio || t.storage_path));
        toast.success(`Shuffled ${reordered.length} tracks — now playing`);
      } else {
        toast.info(`Shuffled ${reordered.length} tracks (none have cloud audio to play)`);
      }
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="h-32 bg-[#F1F5F9] animate-pulse border border-[#E2E8F0] mb-8" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-72 bg-[#F1F5F9] animate-pulse border border-[#E2E8F0]" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!crate) return null;

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back link */}
        <Link to="/crates" className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="font-mono text-sm">Back to Crates</span>
        </Link>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 p-6 border border-[#E2E8F0] bg-white">
          <div className="flex items-center gap-4">
            <div 
              className="w-16 h-16 flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${crate.color}20`, borderColor: crate.color, borderWidth: 2 }}
            >
              <FolderOpen className="w-8 h-8" style={{ color: crate.color }} />
            </div>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">
                {crate.name}
              </h1>
              {crate.description && (
                <p className="font-body text-[#64748B] mt-1">{crate.description}</p>
              )}
              <p className="font-mono text-sm text-[#64748B] mt-2">
                {tracks.length} track{tracks.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Shuffle */}
            {tracks.length >= 2 && (
              <Button
                onClick={handleShuffle}
                variant="outline"
                className="border-[#E2E8F0] text-[#64748B] hover:text-[#2563FF] hover:border-[#2563FF]/50"
                data-testid="shuffle-crate-btn"
                title="Smart Shuffle (Key + Energy)"
              >
                <Shuffle className="w-4 h-4 mr-2" />
                Shuffle
              </Button>
            )}
            {tracks.length > 0 && (
              <Button
                onClick={() => setShowExportModal(true)}
                variant="outline"
                className="border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A]"
                data-testid="export-crate-btn"
                title="Export as M3U8"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            )}
            {/* Add Tracks */}
            <Dialog open={showAddTracksDialog} onOpenChange={setShowAddTracksDialog}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-tracks-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Tracks
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-[#E2E8F0] max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="font-heading text-[#0F172A]">Add Tracks to Crate</DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto space-y-2 py-4">
                  {availableTracks.length === 0 ? (
                    <p className="text-[#64748B] text-center py-8">All tracks are already in this crate</p>
                  ) : (
                    availableTracks.map(track => (
                      <button
                        key={track.id}
                        onClick={() => toggleTrackSelection(track.id)}
                        className={`w-full text-left p-3 border transition-colors flex items-center gap-3 ${
                          selectedToAdd.includes(track.id) 
                            ? 'border-[#2563FF] bg-[#EFF6FF]' 
                            : 'border-[#E2E8F0] hover:border-[#2563FF]/50'
                        }`}
                      >
                        <div className={`w-5 h-5 border-2 flex items-center justify-center ${
                          selectedToAdd.includes(track.id) 
                            ? 'bg-[#2563FF] border-[#2563FF]' 
                            : 'border-gray-600'
                        }`}>
                          {selectedToAdd.includes(track.id) && (
                            <svg className="w-3 h-3 text-[#0F172A]" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-normal text-[15px] text-[#3B82F6] truncate">{track.title}</p>
                          <p className="font-body text-[#64748B] text-sm truncate">{track.artist}</p>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-mono text-[#2563FF]">{track.analysis?.camelot || '--'}</span>
                          <span className="font-mono text-[15px] text-[#64748B]">{track.analysis?.bpm?.toFixed(0) || '--'} BPM</span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
                {availableTracks.length > 0 && (
                  <div className="pt-4 border-t border-[#E2E8F0]">
                    <Button 
                      onClick={addSelectedTracks}
                      disabled={selectedToAdd.length === 0}
                      className="w-full btn-primary"
                      data-testid="confirm-add-tracks"
                    >
                      Add {selectedToAdd.length} Track{selectedToAdd.length !== 1 ? 's' : ''}
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Main content — stacked vertically */}
        <div className="flex flex-col gap-6">
          {/* Tracks section — full width */}
          <div className="w-full min-w-0">
            {/* Tracks Display - List View Only */}
            {tracks.length > 0 ? (
                <div className="space-y-2">
                  {/* Selection toolbar */}
                  {selectedTracks.length > 0 && (
                    <div className="flex items-center gap-3 px-4 py-2 bg-[#EFF6FF] border border-[#DBEAFE] rounded-lg">
                      <span className="font-mono text-xs text-[#2563FF]">{selectedTracks.length} selected</span>
                      <button
                        onClick={removeSelectedFromCrate}
                        className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-red-50 border border-red-200 text-red-600 text-xs font-mono hover:bg-red-100 transition-colors"
                        data-testid="remove-selected-btn"
                      >
                        <Trash2 className="w-3 h-3" />
                        Remove from crate
                      </button>
                      <button
                        onClick={() => setSelectedTracks([])}
                        className="ml-auto text-xs text-[#64748B] hover:text-[#0F172A] font-mono"
                      >
                        Clear
                      </button>
                    </div>
                  )}

                  {/* List header */}
                  <div className="grid px-4 py-2 text-xs font-mono text-[#94A3B8] uppercase tracking-widest border-b border-[#E2E8F0]"
                       style={{gridTemplateColumns: '56px 1fr 52px 76px 52px 170px 40px 56px', gap: '12px'}}>
                    <div></div>
                    <div>Title / Artist</div>
                    <div className="text-center">BPM</div>
                    <div className="text-center">Key</div>
                    <div className="text-center">Energy</div>
                    <div className="text-center">Structure</div>
                    <div></div>
                    <div className="flex items-center justify-center">
                      <button
                        onClick={selectAllCrateTracks}
                        className={`w-4 h-4 flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-all ${
                          selectedTracks.length === tracks.length && tracks.length > 0
                            ? 'bg-[#2563FF] border-[#2563FF]'
                            : selectedTracks.length > 0
                              ? 'bg-[#2563FF]/50 border-[#2563FF]'
                              : 'bg-[#E2E8F0] border-[#94A3B8] hover:border-[#2563FF] hover:bg-[#DBEAFE]'
                        }`}
                        data-testid="select-all-crate-tracks"
                        title={selectedTracks.length === tracks.length ? 'Deselect all' : 'Select all'}
                      >
                        {selectedTracks.length > 0 && (
                          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            {selectedTracks.length === tracks.length ? (
                              <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                            ) : (
                              <path d="M5 11h10v2H5z"/>
                            )}
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {tracks.map((track, index) => (
                    <motion.div
                      key={track.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className={`grid px-4 py-2.5 items-center border hover:border-[#2563FF]/50 transition-colors bg-white group ${
                        selectedTracks.includes(track.id) ? 'border-[#2563FF] bg-[#F1F5F9]' :
                        playingTrack?.id === track.id ? 'border-[#2563FF] bg-[#EFF6FF]' : 'border-[#E2E8F0]'
                      }`}
                      style={{gridTemplateColumns: '56px 1fr 52px 76px 52px 170px 40px 56px', gap: '12px'}}
                    >
                      {/* Cover */}
                      <div>
                        <TrackCoverButton
                          track={track}
                          isCurrentlyPlaying={playingTrack?.id === track.id}
                          isPlaying={isPlaying}
                          onPlayPause={handlePlayTrack}
                          wrapperClassName="w-12 h-12"
                        />
                      </div>

                      {/* Title / Artist / Genre */}
                      <div className="min-w-0">
                        <Link to={`/track/${track.id}`}>
                          <p className="font-medium text-[13px] text-[#3B82F6] truncate hover:text-[#2563FF] transition-colors">
                            {track.title}
                          </p>
                          <p className="font-body text-[11px] text-[#64748B] truncate mt-0.5">{track.artist}</p>
                        </Link>
                        <div className="mt-1">
                          <GenreEditor
                            track={track}
                            onChange={(updated) => setTracks(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t))}
                          />
                        </div>
                      </div>

                      {/* BPM */}
                      <div className="text-center">
                        <span className="font-mono text-base text-[#0F172A]">
                          {track.analysis?.bpm?.toFixed(0) || '--'}
                        </span>
                      </div>

                      {/* Key / Camelot */}
                      <div className="text-center">
                        <span className="font-mono text-base text-[#2563FF] font-bold block">
                          {track.analysis?.camelot || '--'}
                        </span>
                        <span className="font-mono text-[10px] text-[#64748B] block mt-0.5">
                          {track.analysis?.key || '--'}
                        </span>
                      </div>

                      {/* Energy */}
                      <div className="text-center">
                        <span className="font-mono text-base text-[#64748B]">
                          {track.analysis?.energy?.toFixed(0) || '--'}
                        </span>
                      </div>

                      {/* Structure waveform */}
                      <div className="h-[42px] rounded-lg bg-white/70 border border-[#E2E8F0] overflow-hidden relative">
                        <WaveformBars
                          structure={track.analysis?.structure}
                          duration={track.duration || 1}
                          progress={0}
                          trackId={track.id}
                        />
                      </div>

                      {/* Actions */}
                      <div className="flex items-center justify-end gap-1">
                        <div className="relative group/dropdown">
                          <button
                            className="p-1 text-[#94A3B8] hover:text-green-500 transition-colors opacity-0 group-hover:opacity-100"
                            title="Add to playlist"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-[#E2E8F0] shadow-xl z-20 hidden group-hover/dropdown:block">
                            <div className="p-2 border-b border-[#E2E8F0]">
                              <p className="font-mono text-xs text-[#64748B] uppercase">Add to playlist</p>
                            </div>
                            {playlists.length === 0 ? (
                              <p className="p-3 text-sm text-[#64748B]">No playlists yet</p>
                            ) : (
                              playlists.map(playlist => (
                                <button
                                  key={playlist.id}
                                  onClick={() => addTrackToPlaylist(playlist.id, track.id)}
                                  className="w-full px-3 py-2 text-left text-sm text-[#334155] hover:bg-[#EFF6FF] hover:text-[#0F172A] flex items-center gap-2"
                                >
                                  <ListMusic className="w-4 h-4 text-[#2563FF]" />
                                  {playlist.name}
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => removeTrackFromCrate(track.id)}
                          className="p-1 text-[#94A3B8] hover:text-[#2563FF] transition-colors opacity-0 group-hover:opacity-100"
                          title="Remove from crate"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Select */}
                      <div className="flex justify-center">
                        <button
                          onClick={() => toggleCrateSelection(track.id)}
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
                            selectedTracks.includes(track.id)
                              ? 'bg-[#2563FF] border-[#2563FF]'
                              : 'bg-[#E2E8F0] border-[#94A3B8] hover:border-[#2563FF] hover:bg-[#DBEAFE]'
                          }`}
                          data-testid={`select-crate-track-${track.id}`}
                          title={selectedTracks.includes(track.id) ? 'Deselect' : 'Select'}
                        >
                          {selectedTracks.includes(track.id) && (
                            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                            </svg>
                          )}
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
            ) : (
              <div className="text-center py-20 border border-[#E2E8F0]">
                <Music className="w-16 h-16 text-[#1E3A8A] mx-auto mb-4" />
                <p className="font-heading text-xl text-[#64748B] mb-2">No tracks in this crate</p>
                <p className="font-body text-[#94A3B8] text-sm mb-4">
                  Add tracks from your library to get started
                </p>
                <Button onClick={() => setShowAddTracksDialog(true)} className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Tracks
                </Button>
              </div>
            )}
          </div>

          {/* Playlists section — full width below tracks */}
          <div className="w-full bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
              <h3 className="font-heading text-[#0F172A] flex items-center gap-2 text-base font-semibold">
                <ListMusic className="w-4 h-4 text-[#2563FF]" />
                Playlists
              </h3>
              <button
                onClick={() => setShowCreatePlaylist(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#EFF6FF] border border-[#DBEAFE] text-[#2563FF] text-xs font-mono uppercase tracking-wider hover:bg-[#DBEAFE] transition-colors"
                data-testid="create-playlist-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                New Playlist
              </button>
            </div>

            {/* Create Playlist inline form */}
            {showCreatePlaylist && (
              <div className="px-5 py-4 border-b border-[#E2E8F0] bg-[#F8FAFC] flex items-center gap-3">
                <Input
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  placeholder="Playlist name..."
                  className="bg-white border-[#E2E8F0] text-[#0F172A] flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && createPlaylist()}
                  autoFocus
                />
                <Button onClick={createPlaylist} size="sm" className="btn-primary">
                  Create
                </Button>
                <Button
                  onClick={() => { setShowCreatePlaylist(false); setNewPlaylistName(''); }}
                  size="sm" variant="outline" className="border-[#E2E8F0]"
                >
                  Cancel
                </Button>
              </div>
            )}

            {/* Add all tracks to playlist row */}
            {tracks.length > 0 && playlists.length > 0 && (
              <div className="px-5 py-3 border-b border-[#E2E8F0] bg-[#F8FBFF] flex items-center gap-3 flex-wrap">
                <span className="font-mono text-[10px] text-[#94A3B8] uppercase tracking-wider shrink-0">Add all {tracks.length} tracks to:</span>
                {playlists.slice(0, 5).map(playlist => (
                  <button
                    key={playlist.id}
                    onClick={() => addAllTracksToPlaylist(playlist.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-[#E2E8F0] hover:border-[#2563FF]/50 hover:bg-[#EFF6FF] text-sm text-[#475569] hover:text-[#2563FF] transition-colors"
                  >
                    <Plus className="w-3 h-3 text-emerald-500" />
                    {playlist.name}
                  </button>
                ))}
              </div>
            )}

            {/* Playlist cards in a horizontal wrap */}
            {playlists.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <ListMusic className="w-10 h-10 text-[#DBEAFE] mx-auto mb-3" />
                <p className="text-sm text-[#94A3B8] mb-2">No playlists yet</p>
                <button
                  onClick={() => setShowCreatePlaylist(true)}
                  className="text-[#2563FF] text-sm hover:underline"
                >
                  Create your first playlist →
                </button>
              </div>
            ) : (
              <div className="p-5 flex flex-wrap gap-3">
                {playlists.map(playlist => (
                  <Link
                    key={playlist.id}
                    to={`/playlist/${playlist.id}`}
                    data-testid={`playlist-card-${playlist.id}`}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[#E2E8F0] bg-white hover:border-[#2563FF]/50 hover:bg-[#F8FBFF] transition-all group min-w-[180px]"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF] border border-[#DBEAFE] rounded-xl flex items-center justify-center shrink-0">
                      <ListMusic className="w-5 h-5 text-[#2563FF]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm text-[#0F172A] truncate group-hover:text-[#2563FF] transition-colors">
                        {playlist.name}
                      </p>
                      <p className="font-mono text-[10px] text-[#94A3B8]">
                        {playlist.active_track_count ?? (playlist.track_ids?.length || 0)} tracks
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#2563FF] shrink-0 transition-colors" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Audio Player is now mounted globally in App.js via GlobalAudioPlayer */}

      {/* Add padding at bottom when player is visible */}
      {playingTrack && <div className="h-24" />}

      {showExportModal && (
        <ExportModal
          kind="crate"
          id={id}
          name={crate?.name}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
}
